import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { catchError, forkJoin, from, map, mergeMap, of, toArray } from 'rxjs';

import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { Popover, PopoverModule } from 'primeng/popover';
import { Select } from 'primeng/select';
import { TagModule } from 'primeng/tag';

import {
  AvatarAtomComponent,
  ButtonIconAtomComponent,
  InputAtomComponent,
  ProgressSpinnerAtomComponent,
} from '@ls-commons/components';
import { InstallationService } from '@ls-commons/installations';
import { getRoleLabel, isAdminRole, Role } from '@ls-commons/models';
import {
  buildInstallationsWithAssignment,
  UserService,
} from '@ls-commons/users/services';
import { errorToast, successToast } from '@ls-commons/utils/toasts';
import {
  FILES_APIV3_DOMAIN,
  FILES_PANEL_DOMAIN,
} from '../environments/environment.token';
import { UserEditComponent } from '../user-edit/user-edit.component';

interface InstallationOption {
  id: number;
  name: string;
  logo_url: string | null;
}

interface InstallationUserRow {
  assignmentId: number;
  userId: number;
  nombre: string;
  apellidos: string;
  email: string;
  image: string;
  role: number;
  notifyEmail: boolean;
  notifySms: boolean;
}

@Component({
  selector: 'lib-installation-users-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Select,
    TagModule,
    PopoverModule,
    MultiSelectModule,
    AvatarAtomComponent,
    ButtonIconAtomComponent,
    InputAtomComponent,
    ProgressSpinnerAtomComponent,
  ],
  templateUrl: './installation-users-view.component.html',
  styleUrl: './installation-users-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationUsersViewComponent implements OnInit {
  private readonly installationService = inject(InstallationService);
  private readonly userService = inject(UserService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly dialogService = inject(DialogService);
  private readonly filesPanelDomain = inject(FILES_PANEL_DOMAIN);
  private readonly filesApiv3Domain = inject(FILES_APIV3_DOMAIN);

  readonly getRoleLabel = getRoleLabel;

  installations = signal<InstallationOption[]>([]);
  selectedInstallationId = signal<number | null>(null);
  loadingUsers = signal(false);
  private failedLogoIds = signal<Set<number>>(new Set());

  private allUsers = signal<any[]>([]);
  private allAssignments = signal<any[]>([]);
  private assignedUsers = signal<InstallationUserRow[]>([]);

  userSearchTerm = signal('');

  private filteredAssignedUsers = computed(() => {
    const term = this.userSearchTerm().trim().toLowerCase();
    if (!term) return this.assignedUsers();

    return this.assignedUsers().filter(user =>
      `${user.nombre} ${user.apellidos} ${user.email}`
        .toLowerCase()
        .includes(term)
    );
  });

  admins = computed(() =>
    this.filteredAssignedUsers().filter(user => isAdminRole(user.role))
  );
  regularUsers = computed(() =>
    this.filteredAssignedUsers().filter(user => !isAdminRole(user.role))
  );
  hasAssignedUsers = computed(() => this.assignedUsers().length > 0);
  hasResults = computed(
    () => this.admins().length > 0 || this.regularUsers().length > 0
  );
  unassignedUsers = computed(() => {
    const assignedIds = new Set(this.assignedUsers().map(user => user.userId));
    return this.allUsers()
      .filter(user => !assignedIds.has(Number(user.id)))
      // `displayName` se precalcula porque el multiselect lo necesita para la
      // etiqueta de los chips: con `optionLabel="nombre"` los chips saldrían
      // solo con el nombre de pila y no se distinguiría entre dos personas
      // que lo compartan.
      .map(user => ({
        ...user,
        displayName: `${user.nombre ?? ''} ${user.apellidos ?? ''}`.trim(),
      }));
  });

  // Ids (no los objetos de usuario) a propósito: `unassignedUsers` es un
  // computed que se recalcula al recargar los asignados, generando objetos
  // nuevos. Guardando referencias a objetos, la selección quedaría apuntando
  // a instancias obsoletas; con ids esa clase de problema no existe.
  usersToAdd: number[] = [];

  // Etiqueta del botón de guardar: deja claro cuántos se van a añadir.
  get addUsersLabel(): string {
    const count = this.usersToAdd.length;
    if (count <= 1) return 'Añadir';
    return `Añadir ${count} usuarios`;
  }

  ngOnInit(): void {
    this.installationService.getInstallations().subscribe((res: any) => {
      const installations: InstallationOption[] = res?.data ?? [];
      this.installations.set(
        [...installations].sort((a, b) =>
          a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
        )
      );
    });

    this.userService.getUsers().subscribe(users => this.allUsers.set(users));
  }

  initials(user: { nombre: string; apellidos: string }): string {
    return `${user.nombre?.[0] ?? ''}${user.apellidos?.[0] ?? ''}`.toUpperCase();
  }

  private resolveImageUrl(path: string | null | undefined): string {
    if (!path) return '';
    return path.startsWith('http') ? path : this.filesPanelDomain + path;
  }

  installationLogoUrl(installation: InstallationOption): string {
    if (!installation.logo_url || this.failedLogoIds().has(installation.id)) {
      return '';
    }
    return installation.logo_url.startsWith('http')
      ? installation.logo_url
      : this.filesApiv3Domain + installation.logo_url;
  }

  onInstallationLogoError(installationId: number): void {
    this.failedLogoIds.update(ids => new Set(ids).add(installationId));
  }

  onInstallationChange(installationId: number): void {
    this.selectedInstallationId.set(installationId);
    this.userSearchTerm.set('');
    this.loadAssignedUsers(installationId);
  }

  private loadAssignedUsers(installationId: number): void {
    this.loadingUsers.set(true);
    // Las asignaciones (V1) se recargan aquí en cada llamada -- si solo se
    // leyera el signal cacheado en ngOnInit, un cambio de notify_email/sms
    // hecho desde "Asignar Instalaciones" (u otra pestaña) no se reflejaría
    // hasta recargar la página entera.
    forkJoin({
      rows: this.userService.getInstallationsUsersV2(installationId),
      assignments: this.userService.getInstallationsUsers(),
    }).subscribe({
      next: ({ rows, assignments }) => {
        this.allAssignments.set(assignments ?? []);

        const usersById = new Map(
          this.allUsers().map(user => [Number(user.id), user])
        );
        // getInstallationsUsersV2 solo devuelve nombre/apellidos/email/usuarioId;
        // el rol (global) y la fila de asignación (id, notify_email, notify_sms)
        // no vienen en esta respuesta, hay que cruzarlos con el listado completo
        // de usuarios y con las asignaciones (V1) de esta instalación.
        const installationAssignments = (assignments ?? []).filter(
          (a: any) => Number(a.installation_id) === Number(installationId)
        );
        const assignmentByUserId = new Map(
          installationAssignments.map((a: any) => [Number(a.usuario_id), a])
        );

        const mapped: InstallationUserRow[] = (rows ?? []).map((row: any) => {
          const userId = Number(row.usuarioId);
          const user = usersById.get(userId) ?? {};
          const assignment = assignmentByUserId.get(userId) ?? {};
          return {
            assignmentId: assignment.id,
            userId,
            nombre: row.nombre ?? user.nombre ?? '',
            apellidos: row.apellidos ?? user.apellidos ?? '',
            email: row.email ?? user.email ?? '',
            image: this.resolveImageUrl(user.image),
            role: user.role ?? Role.User,
            notifyEmail: assignment.notify_email === 1,
            notifySms: assignment.notify_sms === 1,
          };
        });
        this.assignedUsers.set(mapped);
        this.loadingUsers.set(false);
      },
      error: () => this.loadingUsers.set(false),
    });
  }

  private reloadAssignedUsers(): void {
    const installationId = this.selectedInstallationId();
    if (installationId) this.loadAssignedUsers(installationId);
  }

  editUser(userId: number): void {
    const installationsData = buildInstallationsWithAssignment(
      this.installations(),
      this.allAssignments(),
      userId
    );

    const ref = this.dialogService.open(UserEditComponent, {
      dismissableMask: false,
      header: 'Modificar Usuario',
      data: {
        userId,
        installationsData,
      },
      width: '600px',
    });
    ref.onClose.subscribe((reload: boolean) => {
      if (!reload) return;
      this.userService.getUsers().subscribe(users => {
        this.allUsers.set(users);
        this.reloadAssignedUsers();
      });
    });
  }

  toggleNotifyEmail(row: InstallationUserRow): void {
    this.updateNotify(row, 'notify_email', !row.notifyEmail);
  }

  toggleNotifySms(row: InstallationUserRow): void {
    this.updateNotify(row, 'notify_sms', !row.notifySms);
  }

  private updateNotify(
    row: InstallationUserRow,
    field: 'notify_email' | 'notify_sms',
    value: boolean
  ): void {
    this.userService
      .updateInstallationsUsers({
        id: row.assignmentId,
        [field]: value ? 1 : 0,
      } as any)
      .subscribe({
        next: () => {
          successToast('Notificación actualizada', this.messageService);
          this.reloadAssignedUsers();
        },
        error: () =>
          errorToast(
            'No se ha podido actualizar la notificación',
            this.messageService
          ),
      });
  }

  revokeAccess(row: InstallationUserRow): void {
    this.confirmationService.confirm({
      message: `¿Seguro que quiere revocar el acceso de ${row.nombre} a esta instalación?`,
      header: 'Confirmar',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.userService.deleteInstallationUser(row.assignmentId).subscribe({
          next: () => {
            successToast('Acceso revocado', this.messageService);
            this.reloadAssignedUsers();
          },
          error: () =>
            errorToast('No se ha podido revocar el acceso', this.messageService),
        });
      },
    });
  }

  // Nº de altas simultáneas. La API no tiene endpoint de alta múltiple (es un
  // POST por asignación), así que se lanzan de a pocas en vez de todas de
  // golpe para no castigar al servidor si se seleccionan muchos usuarios.
  private static readonly ADD_CONCURRENCY = 4;

  addUsers(popover: Popover): void {
    const installationId = this.selectedInstallationId();
    const userIds = [...this.usersToAdd];
    if (!installationId || userIds.length === 0) return;

    // No se usa forkJoin: si una sola alta falla, forkJoin corta y se pierde
    // el resultado de las demás (algunas ya habrían quedado creadas en BD sin
    // que el usuario lo supiera). Aquí cada alta captura su propio error, así
    // que al final se sabe exactamente cuántas entraron y cuántas no.
    from(userIds)
      .pipe(
        mergeMap(
          userId =>
            this.userService
              .createInstallationUser({
                installation_id: installationId,
                usuario_id: userId,
                notify_email: 1,
                notify_sms: 0,
              })
              .pipe(
                map(() => true),
                catchError(() => of(false))
              ),
          InstallationUsersViewComponent.ADD_CONCURRENCY
        ),
        toArray()
      )
      .subscribe(results => {
        const added = results.filter(ok => ok).length;
        const failed = results.length - added;

        if (added > 0) {
          successToast(
            added === 1
              ? 'Usuario añadido a la instalación'
              : `${added} usuarios añadidos a la instalación`,
            this.messageService
          );
        }

        // Los fallos se informan siempre, incluso si parte ha ido bien: si no,
        // el usuario vería el toast de éxito y daría por añadidos a todos.
        if (failed > 0) {
          errorToast(
            failed === 1
              ? 'No se ha podido añadir 1 usuario'
              : `No se han podido añadir ${failed} usuarios`,
            this.messageService
          );
        }

        this.usersToAdd = [];
        popover.hide();
        this.reloadAssignedUsers();
      });
  }
}
